import type { MerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  getSettingCatalogGroup,
  type SettingGroupFullPageTarget,
} from "@redotech/redo-settings-common/settings-catalog";
import { resolveReturnsSettingsFullPageTarget } from "@redotech/redo-settings-common/settings-catalog/returns-new-settings";
import { assertNever } from "@redotech/util/type";
import { getSettingsFocusedSettingHref } from "./settings-deep-links";

type SettingsGroupFullPageRoutes = Pick<
  MerchantRoutes,
  | "contextualHref"
  | "getSettingsSubRoute"
  | "getOrdersOutboundLabelsSubRoute"
  | "legacySupportBaseUrl"
  | "settingsHref"
>;

export function getSettingsGroupFullPageHref({
  groupId,
  routes,
  shippingNewSettings = false,
  returnsNewSettings = false,
}: {
  groupId: string;
  routes: SettingsGroupFullPageRoutes;
  shippingNewSettings?: boolean;
  /** The merchant is on the new returns settings page, not the old pages. */
  returnsNewSettings?: boolean;
}): string {
  return resolveSettingsGroupFullPageHref({
    fullPageTarget: resolveReturnsSettingsFullPageTarget({
      groupId,
      fullPageTarget: getSettingCatalogGroup(groupId, shippingNewSettings)
        ?.fullPageTarget,
      returnsNewSettings,
    }),
    routes,
  });
}

export function getSettingsFocusedGroupSettingHref({
  groupId,
  routes,
  settingId,
  returnsNewSettings,
}: {
  groupId: string;
  routes: SettingsGroupFullPageRoutes;
  settingId: string;
  returnsNewSettings: boolean;
}): string {
  return getSettingsFocusedSettingHref({
    groupId,
    href: getSettingsGroupFullPageHref({ groupId, routes, returnsNewSettings }),
    settingId,
  });
}

function resolveSettingsGroupFullPageHref({
  fullPageTarget,
  routes,
}: {
  fullPageTarget: SettingGroupFullPageTarget | undefined;
  routes: SettingsGroupFullPageRoutes;
}): string {
  if (!fullPageTarget) {
    return routes.settingsHref;
  }

  switch (fullPageTarget.type) {
    case "settings":
      return routes.getSettingsSubRoute(fullPageTarget.path);
    case "legacySupport":
      return `${routes.legacySupportBaseUrl}${fullPageTarget.path}`;
    case "settingsHome":
      return routes.settingsHref;
    case "scanToVerify":
      return `${routes.getOrdersOutboundLabelsSubRoute("scan")}?settings=scan-to-verify`;
    case "contextual":
      return routes.contextualHref;
    default:
      assertNever(fullPageTarget);
  }
}
